import { Fragment, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NodeIds } from '@breathelife/insurance-form-builder';
import {
  AgeRoundingTypes,
  BlueprintCollectionOperator,
  BlueprintCollectionOperators,
  BlueprintConditionValue,
  BlueprintSingleConditionValue,
  BooleanOperators,
  ConditionBlueprintType,
  DateUnits,
  isBlueprintConditionsValue,
  NumberComparisonConditionOperators,
} from '@zinnia/form-engine-sdk';

import { ReactHtmlParser } from '@breathelife/ui-components';
import Typography from '../../Components/Typography';
import { BooleanOperatorView } from './Helpers/BooleanOperator';

type SingleConditionProps = {
  condition: BlueprintSingleConditionValue;
};

type ConditionsProps = {
  condition?: BlueprintConditionValue;
};

const ROUNDING_TYPES_LABEL_KEYS = {
  [AgeRoundingTypes.closestBirthday]: 'admin.conditions.labels.roundingTypeClosestBirthday',
  [AgeRoundingTypes.lastBirthday]: 'admin.conditions.labels.roundingTypeLastBirthday',
  [AgeRoundingTypes.nextBirthday]: 'admin.conditions.labels.roundingTypeNextBirthday',
  [AgeRoundingTypes.none]: 'admin.conditions.labels.roundingTypeNone',
};

const UNIT_LABEL_KEYS = {
  [DateUnits.year]: 'admin.conditions.labels.years',
  [DateUnits.month]: 'admin.conditions.labels.months',
  [DateUnits.day]: 'admin.conditions.labels.days',
};

function SingleConditionView({ condition }: SingleConditionProps): ReactElement {
  const { t } = useTranslation();

  const operatorKey = useMemo(() => {
    // Note: This ignores any aggregate conditions on insured people. This should be adapted someday to display that information.

    let collectionOperators: Record<string, BlueprintCollectionOperator> | undefined = undefined;
    if (condition.type === ConditionBlueprintType.context) {
      if (condition.right.type === 'NodeId') {
        collectionOperators = condition.right.collectionOperators;
      }
    } else {
      collectionOperators = condition.collectionOperators;
    }
    const firstNonInsuredCollectionNodeId = collectionOperators
      ? Object.keys(collectionOperators).find((collectionNodeId) => collectionNodeId !== NodeIds.insuredPeople)
      : undefined;
    const firstNonInsuredCollectionOperator = firstNonInsuredCollectionNodeId
      ? collectionOperators?.[firstNonInsuredCollectionNodeId]
      : undefined;

    return firstNonInsuredCollectionOperator &&
      firstNonInsuredCollectionOperator !== BlueprintCollectionOperators.thisItem
      ? firstNonInsuredCollectionOperator
      : 'default';
  }, [condition]);

  switch (condition.type) {
    case ConditionBlueprintType.ageRange: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              fieldName: condition.targetBirthdateNodeId,
              minAge: condition.value.minAge,
              maxAge: condition.value.maxAge,
              unit: t(UNIT_LABEL_KEYS[condition.value.unit as 'year' | 'month' | 'day']).toLowerCase(),
              roundingType: condition.roundingType
                ? t(ROUNDING_TYPES_LABEL_KEYS[condition.roundingType]).toLowerCase()
                : '',
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.bmiRange: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              minBmi: condition.value.minBMI,
              maxBmi: condition.value.maxBMI,
              heightField: condition.heightNodeId,
              weightField: condition.weightNodeId,
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.characterCountInBetween: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              fieldName: condition.targetNodeId,
              minLength: condition.value.minLength || '0',
              maxLength: condition.value.maxLength || t('admin.conditions.labels.noUpperLimit'),
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.countEqual: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(prefixKey, {
              targetNodeId: condition.targetNodeId,
              controlValue: condition.controlValue,
            })}
          />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.dateComparison: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;
      const dateUnit = t(`admin.conditions.options.dateUnit.${condition.unit}`).toLowerCase();

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(prefixKey, {
              startDateFieldName: condition.startDateNodeId,
              endDateFieldName: condition.endDateNodeId,
            })}
          />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value, dateUnit })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.emptiness: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}.${condition.isEmpty}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              fieldName: condition.targetNodeId,
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.equality: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}.${condition.isEqual}`;

      const textOptions = {
        fieldName: condition.targetNodeId,
        value: condition.value || condition.nodeIdOfValue,
      };

      return (
        <Typography variant='body1'>
          <ReactHtmlParser html={t(textKey, textOptions)} />
        </Typography>
      );
    }

    case ConditionBlueprintType.futureDate: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser html={t(prefixKey, { fieldName: condition.targetNodeId })} />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value, dateUnit: condition.unit })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.lastIncidentDate: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser html={t(prefixKey, { fieldName: condition.targetNodeId })} />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value, dateUnit: condition.unit })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.matches: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}.${condition.quantifier}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              fieldName: condition.targetNodeId,
              formattedValues: condition.value.join(', '),
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.matchesRegex: {
      const key = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(key, {
              fieldName: condition.targetNodeId,
              regex: condition.regex,
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.luhnCheck: {
      const key = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(key, {
              fieldName: condition.targetNodeId,
            })}
          />
        </Typography>
      );
    }

    case ConditionBlueprintType.mathOperator: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}.${condition.mathOperator}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;
      const fieldNames = condition.nodeIds.join(', ');

      return (
        <Typography variant='body1'>
          <ReactHtmlParser html={t(prefixKey, { fieldNames })} />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value || condition.nodeIdOfValue })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.numberComparison: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;
      const numberComparisonOperatorKey = `admin.conditions.options.numberComparison.${condition.operator}`;

      const textOptions = {
        fieldName: condition.targetNodeId,
        operator: t(numberComparisonOperatorKey).toLowerCase(),
        conjunction: [
          NumberComparisonConditionOperators.equal,
          NumberComparisonConditionOperators.lessThanOrEqual,
          NumberComparisonConditionOperators.greaterThanOrEqual,
        ].includes(condition.operator as 'equal' | 'lessThanOrEqual' | 'greaterThanOrEqual')
          ? t('to')
          : null,
        value: condition.value || condition.nodeIdOfValue,
      };

      return (
        <Typography variant='body1'>
          <ReactHtmlParser html={t(textKey, textOptions)} />
        </Typography>
      );
    }

    case ConditionBlueprintType.percentOf: {
      const prefixKey = `admin.conditions.labels.conditionSummary.${condition.type}.prefix.${operatorKey}`;
      const suffixKey = `admin.conditions.labels.conditionSummary.${condition.type}.suffix.${condition.operator}`;

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(prefixKey, {
              targetNodeId: condition.targetNodeId,
              percent: condition.percent,
            })}
          />
          <ReactHtmlParser html={t(suffixKey, { value: condition.value || condition.nodeIdOfValue })} />
        </Typography>
      );
    }

    case ConditionBlueprintType.reflexive: {
      const textKey = `admin.conditions.labels.conditionSummary.${condition.type}.${operatorKey}`;
      const valueLabel = t(`admin.conditions.options.yesNo.${condition.value}`);

      return (
        <Typography variant='body1'>
          <ReactHtmlParser
            html={t(textKey, {
              fieldName: condition.targetNodeId,
              value: valueLabel,
            })}
          />
        </Typography>
      );
    }
  }
  return <Fragment />;
}

export function ConditionView({ condition, depth = 0 }: ConditionsProps & { depth?: number }): ReactElement | null {
  const { t } = useTranslation();
  if (!condition) {
    return null;
  }

  if (isBlueprintConditionsValue(condition)) {
    return (
      <span>
        {depth > 0 && condition.booleanOperator !== BooleanOperators.not ? '(' : null}
        {condition.booleanOperator === BooleanOperators.not ? `${t(condition.booleanOperator).toUpperCase()} (` : null}
        {condition.conditions.map((nestedCondition, index) => {
          return (
            <Fragment key={`condition-${depth}.${index}`}>
              <ConditionView condition={nestedCondition} depth={depth + 1} />
              <BooleanOperatorView conditions={condition} index={index} />
            </Fragment>
          );
        })}
        {depth > 0 || condition.booleanOperator === BooleanOperators.not ? ')' : null}
      </span>
    );
  }

  return <SingleConditionView condition={condition} />;
}
